import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  FiFolder, 
  FiCheckCircle, 
  FiGitBranch, 
  FiBarChart2, 
  FiPackage,
  FiX,
  FiHome,
  FiChevronDown,
  FiChevronRight
} from 'react-icons/fi';

const Sidebar = ({ activeView, onViewChange, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const explorerSubmenuItems = useMemo(() => [
    {
      id: 'validation',
      label: 'Validation',
      icon: FiCheckCircle,
      path: '/validation',
      description: 'Validate data integrity'
    },
    {
      id: 'version',
      label: 'Version Control',
      icon: FiGitBranch,
      path: '/version',
      description: 'Track file changes'
    },
    {
      id: 'visualization',
      label: 'Visualization',
      icon: FiBarChart2,
      path: '/visualization',
      description: 'View data insights'
    }
  ], []);

  const isExplorerRelatedRoute = useCallback((pathname) => {
    return pathname === '/explorer' || explorerSubmenuItems.some((item) => item.path === pathname);
  }, [explorerSubmenuItems]);

  const [isExplorerExpanded, setIsExplorerExpanded] = useState(() => isExplorerRelatedRoute(location.pathname));

  useEffect(() => {
    if (isExplorerRelatedRoute(location.pathname)) {
      setIsExplorerExpanded(true);
    }
  }, [location.pathname, isExplorerRelatedRoute]);

  const menuItems = [
    {
      id: 'explorer',
      label: 'File Explorer',
      icon: FiFolder,
      path: '/explorer',
      description: 'Browse and manage data files',
      children: explorerSubmenuItems
    },
    {
      id: 'packaging',
      label: 'Packaging',
      icon: FiPackage,
      path: '/packaging',
      description: 'Create data packages'
    }
  ];

  const handleMenuItemClick = (item) => {
    if (item.id === 'explorer') {
      setIsExplorerExpanded((prev) => !prev);
    }

    onViewChange(item.id);
    navigate(item.path);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      className="theme-sidebar h-full flex flex-col"
    >
      {/* Sidebar Header */}
      <div className="p-6 border-b border-secondary-200">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-secondary-800">Navigation</h2>
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-secondary-100 lg:hidden"
          >
            <FiX className="w-5 h-5 text-secondary-600" />
          </button>
        </div>
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 p-4 space-y-2">
        {menuItems.map((item, index) => {
          const Icon = item.icon;
          const active = item.id === 'explorer'
            ? isExplorerRelatedRoute(location.pathname)
            : isActive(item.path);
          const hasChildren = Array.isArray(item.children) && item.children.length > 0;
          
          return (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <motion.button
                whileHover={{ scale: 1.02, x: 4 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleMenuItemClick(item)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                  active
                    ? 'bg-primary-50 text-primary-700 border-l-4 border-primary-600'
                    : 'hover:bg-secondary-50 text-secondary-700'
                }`}
              >
                <Icon className={`w-5 h-5 ${active ? 'text-primary-600' : 'text-secondary-500'}`} />
                <div className="flex-1 text-left">
                  <div className={`font-medium ${active ? 'text-primary-700' : 'text-secondary-800'}`}>
                    {item.label}
                  </div>
                  <div className="text-xs text-secondary-500">
                    {item.description}
                  </div>
                </div>

                {hasChildren && (
                  isExplorerExpanded
                    ? <FiChevronDown className="w-4 h-4 text-secondary-500" />
                    : <FiChevronRight className="w-4 h-4 text-secondary-500" />
                )}
              </motion.button>

              {hasChildren && isExplorerExpanded && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="mt-2 ml-4 pl-3 border-l border-secondary-200 space-y-1"
                >
                  {item.children.map((child) => {
                    const ChildIcon = child.icon;
                    const childActive = isActive(child.path);

                    return (
                      <motion.button
                        key={child.id}
                        whileHover={{ scale: 1.01, x: 2 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleMenuItemClick(child)}
                        className={`w-full flex items-center space-x-3 px-3 py-2 rounded-md transition-all duration-200 ${
                          childActive
                            ? 'bg-primary-50 text-primary-700 border-l-2 border-primary-600'
                            : 'hover:bg-secondary-50 text-secondary-700'
                        }`}
                      >
                        <ChildIcon className={`w-4 h-4 ${childActive ? 'text-primary-600' : 'text-secondary-500'}`} />
                        <div className="flex-1 text-left">
                          <div className={`text-sm font-medium ${childActive ? 'text-primary-700' : 'text-secondary-800'}`}>
                            {child.label}
                          </div>
                          <div className="text-xs text-secondary-500">
                            {child.description}
                          </div>
                        </div>
                      </motion.button>
                    );
                  })}
                </motion.div>
              )}
            </motion.div>
          );
        })}
      </nav>

      {/* Sidebar Footer */}
      <div className="p-4 border-t border-secondary-200">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => {
            onViewChange('explorer');
            navigate('/explorer');
          }}
          className="w-full flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-secondary-50 text-secondary-700 transition-colors"
        >
          <FiHome className="w-4 h-4" />
          <span className="text-sm font-medium">Back to Home</span>
        </motion.button>
      </div>
    </motion.div>
  );
};

export default Sidebar;
